/**
 * RuleCard Component
 *
 * Displays a single learned rule with:
 * - Prominent mandate badge
 * - Confidence-based status indicator
 * - Pattern information
 * - Performance metrics
 * - Action buttons
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import type { LearnedRule } from '@/types/learningRules';
import { Edit, Power, Share2, Copy, Info } from 'lucide-react';

interface RuleCardProps {
  rule: LearnedRule;
  mandateName?: string;
  onEdit?: (rule: LearnedRule) => void;
  onToggleStatus?: (rule: LearnedRule) => void;
  onShare?: (rule: LearnedRule) => void;
  onCopy?: (rule: LearnedRule) => void;
  onViewDetails?: (rule: LearnedRule) => void;
}

export const RuleCard: React.FC<RuleCardProps> = ({
  rule,
  mandateName,
  onEdit,
  onToggleStatus,
  onShare,
  onCopy,
  onViewDetails,
}) => {
  // Check if this is a hardcoded system rule
  const isSystemRule = rule.id.startsWith('hardcoded-');

  // Calculate success rate
  const successRate = rule.usage_count > 0
    ? (rule.success_count / rule.usage_count) * 100
    : 0;

  // Determine confidence color and status
  const getConfidenceStatus = () => {
    const confidence = rule.current_confidence * 100;
    if (confidence >= 90) return { color: 'bg-green-500', emoji: '🟢', label: 'Excellent' };
    if (confidence >= 75) return { color: 'bg-yellow-500', emoji: '🟡', label: 'Good' };
    return { color: 'bg-red-500', emoji: '🔴', label: 'Needs Review' };
  };

  const confidenceStatus = getConfidenceStatus();

  // Pattern type label
  const getPatternTypeLabel = () => {
    switch (rule.pattern_type) {
      case 'counterparty': return 'Counterparty';
      case 'description_pattern': return 'Description';
      case 'amount_based': return 'Amount-Based';
      case 'semantic': return 'Semantic';
      default: return rule.pattern_type;
    }
  };

  // Mandate badge color (you can customize this based on mandate_id)
  const getMandateBadgeColor = () => {
    if (!mandateName) return 'bg-gray-500';
    // Simple hash-based color selection
    const hash = mandateName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-orange-500', 'bg-purple-500', 'bg-pink-500'];
    return colors[hash % colors.length];
  };

  return (
    <Card className={`hover:shadow-md transition-shadow ${!rule.is_active ? 'opacity-60' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          {/* Left: Mandate Badge + Status */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* PROMINENT MANDATE/SYSTEM BADGE */}
            {isSystemRule ? (
              <Badge className="bg-purple-600 text-white px-3 py-1 text-xs font-semibold">
                🌍 System Rule
              </Badge>
            ) : (
              <Badge className={`${getMandateBadgeColor()} text-white px-3 py-1 text-xs font-semibold`}>
                {mandateName || 'Unknown Mandate'}
              </Badge>
            )}

            {/* Confidence Status Indicator */}
            <div className="flex items-center gap-1">
              <span className="text-lg">{confidenceStatus.emoji}</span>
              <span className="text-sm font-medium text-muted-foreground">
                {confidenceStatus.label}
              </span>
            </div>

            {/* Shared Badge */}
            {rule.is_shared && (
              <Badge variant="secondary" className="text-xs">
                <Share2 className="w-3 h-3 mr-1" />
                Shared
              </Badge>
            )}

            {/* Inactive Badge */}
            {!rule.is_active && (
              <Badge variant="destructive" className="text-xs">
                Inactive
              </Badge>
            )}

            {/* Deprecated Badge */}
            {rule.is_deprecated && (
              <Badge variant="destructive" className="text-xs">
                Deprecated
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Pattern Information */}
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">Pattern:</span>
            <Badge variant="outline" className="text-xs">
              {getPatternTypeLabel()}
            </Badge>
          </div>
          <div className="text-base font-semibold">
            {rule.pattern_key}
            {rule.pattern_value && (
              <span className="text-muted-foreground ml-2">
                → {rule.pattern_value}
              </span>
            )}
          </div>
        </div>

        {/* Accounting Classification */}
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-muted-foreground">Debit:</span>
            <span className="ml-2 font-medium">{rule.suggested_debit_account}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Credit:</span>
            <span className="ml-2 font-medium">{rule.suggested_credit_account}</span>
          </div>
        </div>

        {/* VAT Information */}
        {rule.vat_code && (
          <div className="text-sm">
            <span className="text-muted-foreground">VAT:</span>
            <span className="ml-2 font-medium">{rule.vat_code}</span>
            {rule.vat_account && (
              <span className="text-muted-foreground ml-2">
                (Account: {rule.vat_account})
              </span>
            )}
          </div>
        )}

        {/* Performance Metrics */}
        <div className="flex items-center gap-4 text-sm pt-2 border-t">
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">Confidence:</span>
            <span className="font-semibold text-base">
              {(rule.current_confidence * 100).toFixed(0)}%
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">Success:</span>
            <span className="font-semibold">
              {rule.success_count}/{rule.usage_count}
            </span>
            <span className="text-muted-foreground">
              ({successRate.toFixed(0)}%)
            </span>
          </div>
          {rule.correction_count > 0 && (
            <div className="flex items-center gap-1 text-orange-600">
              <span>⚠️ {rule.correction_count} corrections</span>
            </div>
          )}
        </div>

        {/* Usage Stats */}
        <div className="text-xs text-muted-foreground">
          Used {rule.usage_count}x
          {rule.learned_from_timestamp && (
            <span className="ml-2">
              • Learned {new Date(rule.learned_from_timestamp).toLocaleDateString()}
            </span>
          )}
        </div>

        {/* Deprecated Reason */}
        {rule.is_deprecated && rule.deprecated_reason && (
          <div className="text-sm text-destructive bg-destructive/10 p-2 rounded">
            <strong>Deprecated:</strong> {rule.deprecated_reason}
          </div>
        )}
      </CardContent>

      <CardFooter className="flex flex-wrap gap-2 pt-3 border-t">
        {/* Action Buttons - Disabled for system rules */}
        {!isSystemRule && onEdit && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(rule)}
            className="flex items-center gap-1"
          >
            <Edit className="w-4 h-4" />
            Edit
          </Button>
        )}

        {!isSystemRule && onToggleStatus && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onToggleStatus(rule)}
            className="flex items-center gap-1"
          >
            <Power className="w-4 h-4" />
            {rule.is_active ? 'Disable' : 'Enable'}
          </Button>
        )}

        {!isSystemRule && onShare && !rule.is_shared && rule.is_active && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onShare(rule)}
            className="flex items-center gap-1"
          >
            <Share2 className="w-4 h-4" />
            Share
          </Button>
        )}

        {!isSystemRule && onCopy && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCopy(rule)}
            className="flex items-center gap-1"
          >
            <Copy className="w-4 h-4" />
            Copy to Mandate
          </Button>
        )}

        {onViewDetails && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewDetails(rule)}
            className="flex items-center gap-1 ml-auto"
          >
            <Info className="w-4 h-4" />
            Details
          </Button>
        )}

        {/* Info text for system rules */}
        {isSystemRule && (
          <div className="text-xs text-muted-foreground italic ml-auto">
            System rules cannot be edited or disabled
          </div>
        )}
      </CardFooter>
    </Card>
  );
};

export default RuleCard;
